import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Search, ChevronUp, ChevronDown } from 'lucide-react';

const FILTER_PANEL_WIDTH = 288; // matches w-72
// Show the search box once the list is long enough to need it.
const FILTER_SEARCH_THRESHOLD = 5;

/**
 * Positions a portal-rendered filter panel next to its trigger and keeps it inside
 * the viewport: clamps horizontally, and flips above the trigger when the space
 * below is too short (filters live in a table header, so a panel opened near the
 * bottom of a long table would otherwise render off-screen). Re-runs on
 * scroll/resize while open so the panel tracks its trigger.
 */
export function useFilterPanelPlacement(open: boolean, btnRef: React.RefObject<HTMLElement | null>, width: number, align: 'left' | 'right' = 'left') {
  const [pos, setPos] = useState({ top: 0, left: 0, maxHeight: 320 });

  const place = React.useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom - 12;
    const above = rect.top - 12;
    const flip = below < 220 && above > below;
    const maxHeight = Math.max(180, Math.min(360, flip ? above : below));
    const preferredLeft = align === 'right' ? rect.right - width : rect.left;
    setPos({
      top: flip ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4,
      left: Math.max(8, Math.min(preferredLeft, window.innerWidth - width - 8)),
      maxHeight,
    });
  }, [btnRef, width, align]);

  useEffect(() => {
    if (!open) return;
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return pos;
}

/**
 * Column filter popover (faceted-filter pattern): searchable multi-select with the
 * selected values pinned above the rest, a count badge on the trigger and a sticky
 * footer. Options arrive in the order the caller supplies; only selected-pinning
 * reorders them.
 */
export function FilterDropdown({ options, selected, onChange, label }: { options: string[]; selected: string[]; onChange: (v: string[]) => void; label?: string }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const dropRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);
  const pos = useFilterPanelPlacement(open, btnRef, FILTER_PANEL_WIDTH);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (btnRef.current?.contains(e.target as Node)) return;
      if (dropRef.current && !dropRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) { setQuery(''); return; }
    searchRef.current?.focus();
  }, [open]);

  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt];
    onChange(next);
  };

  const showSearch = options.length > FILTER_SEARCH_THRESHOLD;
  const q = query.trim().toLowerCase();
  const matches = q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  // Selected first so an active filter stays visible without scrolling.
  const picked = matches.filter((o) => selected.includes(o));
  const rest = matches.filter((o) => !selected.includes(o));

  const row = (opt: string) => {
    const checked = selected.includes(opt);
    return (
      <button
        key={opt}
        type="button"
        onClick={() => toggle(opt)}
        title={opt}
        className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2 ${checked ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-700 dark:text-gray-300'}`}
      >
        <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300 dark:border-gray-600'}`}>
          {checked && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>}
        </div>
        <span className="truncate">{opt}</span>
      </button>
    );
  };

  return (
    <span className="inline-block ml-1">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(!open)}
        aria-label={label ? `Filter by ${label}` : 'Filter'}
        className={`inline-flex items-center gap-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors ${selected.length > 0 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-300 dark:text-gray-600 hover:text-gray-500'}`}
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" /></svg>
        {selected.length > 0 && (
          <span className="px-1 min-w-[14px] rounded-full bg-blue-600 text-white text-[9px] leading-[14px] font-semibold normal-case">{selected.length}</span>
        )}
      </button>
      {open && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="fixed w-72 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg font-normal text-left text-sm flex flex-col overflow-hidden"
          style={{ top: pos.top, left: pos.left, maxHeight: pos.maxHeight, zIndex: 9999 }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); btnRef.current?.focus(); }
            if (e.key === 'Enter' && matches.length > 0) { e.preventDefault(); toggle([...picked, ...rest][0]); }
          }}
        >
          {label && (
            <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400">{label}</div>
          )}
          {showSearch && (
            <div className="px-2 pt-1.5 pb-2 border-b border-gray-100 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={label ? `Search ${label.toLowerCase()}…` : 'Search…'}
                  className="w-full pl-7 pr-2 py-1.5 text-xs rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 normal-case"
                />
              </div>
            </div>
          )}
          <div className="flex-1 overflow-y-auto py-1">
            {matches.length === 0 && (
              <div className="px-3 py-4 text-xs text-center text-gray-400 dark:text-gray-500 normal-case">No matches</div>
            )}
            {picked.map(row)}
            {picked.length > 0 && rest.length > 0 && <div className="my-1 border-t border-gray-100 dark:border-gray-800" />}
            {rest.map(row)}
          </div>
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/40">
            <span className="text-[11px] text-gray-500 dark:text-gray-400 normal-case">
              {selected.length > 0 ? `${selected.length} selected` : `${options.length} option${options.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={selected.length === 0}
              className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:text-gray-300 dark:disabled:text-gray-600 disabled:no-underline disabled:cursor-default normal-case"
            >
              Clear
            </button>
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}

export function SortFilterHeader({ label, sortKey, currentSort, onSort, filterOptions, filterValue, onFilter, customFilter, className }: {
  label: string; sortKey: string;
  currentSort: { col: string; dir: 'asc' | 'desc' } | null;
  onSort: (col: string) => void;
  filterOptions?: string[];
  filterValue?: string[];
  onFilter?: (v: string[]) => void;
  customFilter?: React.ReactNode;
  className?: string;
}) {
  const isActive = currentSort?.col === sortKey;
  return (
    <th className={`px-4 py-3 font-semibold select-none ${className || ''}`}>
      <div className={`flex items-center gap-1.5 ${className?.includes('text-center') ? 'justify-center' : ''}`}>
        <div className="flex items-center gap-1.5 cursor-pointer hover:text-gray-900 dark:hover:text-gray-100" onClick={() => onSort(sortKey)}>
          {label}
          <div className="flex flex-col">
            <ChevronUp className={`w-2 h-2 -mb-0.5 ${isActive && currentSort?.dir === 'asc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
            <ChevronDown className={`w-2 h-2 ${isActive && currentSort?.dir === 'desc' ? 'text-blue-600' : 'text-gray-300 dark:text-gray-600'}`} />
          </div>
        </div>
        {customFilter ?? (filterOptions && onFilter && (
          <FilterDropdown options={filterOptions} selected={filterValue ?? []} onChange={onFilter} label={label} />
        ))}
      </div>
    </th>
  );
}
